// Shared constants + helpers for the automatic-approval rules (the CategoryRules list
// and the rule modal). One place so the list, modal and summaries read the same labels.

// Friendly, merchant-facing labels for the normalized dispute categories, used when a
// rule has a "Category is X" condition.
export const CATEGORY_LABELS = {
  fraudulent: "Fraud / Card Not Present",
  product_not_received: "Merchandise / Services Not Received",
  subscription_canceled: "Cancelled Recurring / Subscription",
  product_unacceptable: "Product Not as Described / Unacceptable",
  credit_not_processed: "Processing Errors (e.g., incorrect amount)",
  duplicate: "Duplicate Processing",
  unrecognized: "Unrecognized",
  general: "General"
}

// A rule's action.
export const ACTION_LABELS = {
  auto: "Auto-submit",
  manual: "Manual Review"
}

// Condition field / operator labels. OP_LABELS is title-case for the dropdown; OP_SUMMARY
// is lower-case for the mid-sentence summary ("Amount greater or equal to $500.00").
export const FIELD_LABELS = {
  amount: "Amount",
  category: "Category",
  issuer: "Issuer",
  card_network: "Card network",
  payment_processor: "Payment processor",
  dispute_type: "Type"
}
// Amount now offers the INCLUSIVE operators only (gte / lte): a merchant thinking
// "everything from $500 up" shouldn't have to enter 499.99. The exclusive gt / lt stay
// in these maps (but are no longer offered, see ConditionsBuilder) so rules saved
// before the change still render with a correct label.
export const OP_LABELS = {
  gte: "Greater or equal to",
  lte: "Less or equal to",
  gt: "Greater than",
  lt: "Less than",
  is: "Is",
  is_not: "Is not"
}
export const OP_SUMMARY = {
  gte: "greater or equal to",
  lte: "less or equal to",
  gt: "greater than",
  lt: "less than",
  is: "is",
  is_not: "is not"
}

export const NETWORK_OPTIONS = [
  { value: "visa", label: "Visa" },
  { value: "mastercard", label: "Mastercard" },
  { value: "amex", label: "Amex" },
  { value: "discover", label: "Discover" }
]
export const ISSUER_OPTIONS = [
  { value: "chase", label: "Chase" },
  { value: "bank_of_america", label: "Bank of America" },
  { value: "capital_one", label: "Capital One" },
  { value: "citi", label: "Citi" },
  { value: "wells_fargo", label: "Wells Fargo" }
]
// Only the processors Recover actually supports today. A rule saved earlier against a
// processor that is no longer offered still renders (conditionSummary falls back to the
// raw value), it just can't be picked again.
export const PROCESSOR_OPTIONS = [
  { value: "stripe", label: "Stripe" },
  { value: "shopify", label: "Shopify" }
]
// Case type (Dispute vs Inquiry), the same closed set the table's Type column renders.
export const TYPE_OPTIONS = [
  { value: "dispute", label: "Dispute" },
  { value: "inquiry", label: "Inquiry" }
]

const optLabel = (options, value) => (options.find((o) => o.value === value) || {}).label || value

// One condition -> readable sentence fragment, e.g. "Amount greater than $500.00",
// "Card network is Visa". `t` translates the labels.
export const conditionSummary = (cond, t) => {
  const field = t(FIELD_LABELS[cond.field] || cond.field)
  const op = t(OP_SUMMARY[cond.op] || cond.op)
  let val
  if (cond.field === "amount") val = `$${Number(cond.value || 0).toFixed(2)}`
  else if (cond.field === "card_network") val = t(optLabel(NETWORK_OPTIONS, cond.value))
  else if (cond.field === "issuer") val = t(optLabel(ISSUER_OPTIONS, cond.value))
  else if (cond.field === "payment_processor") val = t(optLabel(PROCESSOR_OPTIONS, cond.value))
  else if (cond.field === "dispute_type") val = t(optLabel(TYPE_OPTIONS, cond.value))
  else if (cond.field === "category") val = t(CATEGORY_LABELS[cond.value] || cond.value)
  else val = String(cond.value)
  return `${field} ${op} ${val}`
}

// All of a rule's conditions joined ("Category is Fraud and Amount greater than $500.00").
// Empty -> "Any dispute" (the rule matches everything).
export const ruleConditionsSummary = (conditions, t) => {
  const list = (conditions || []).filter((c) => c.value !== "" && c.value != null)
  if (!list.length) return t("Any dispute")
  return list.map((c) => conditionSummary(c, t)).join(t(" and "))
}

// A rule's conditions are AND-ed, so some combinations can never match a real dispute.
// Return the first human-readable conflict message, or '' when the set is satisfiable.
// Covers: duplicate amount bounds, an empty amount range, and match fields that both
// require and exclude a value or require two different values at once.
export const conditionsConflict = (conditions, t) => {
  const list = (conditions || []).filter((c) => c && c.field)

  // Amount: at most one lower bound and one upper bound, and the range must be
  // non-empty. Both the inclusive (gte/lte) and legacy exclusive (gt/lt) operators
  // count, since a saved rule may still carry the old ones.
  const amounts = list.filter((c) => c.field === "amount")
  const gts = amounts.filter((c) => c.op === "gte" || c.op === "gt")
  const lts = amounts.filter((c) => c.op === "lte" || c.op === "lt")
  if (gts.length > 1) return t("Use at most one lower amount bound.")
  if (lts.length > 1) return t("Use at most one upper amount bound.")
  const hasVal = (c) => c && c.value !== "" && c.value != null
  if (gts.length && lts.length && hasVal(gts[0]) && hasVal(lts[0])) {
    const lo = Number(gts[0].value)
    const hi = Number(lts[0].value)
    // Both bounds inclusive: lo === hi still matches exactly that amount, so only
    // lo > hi is empty. If either bound is exclusive, lo === hi matches nothing.
    const bothInclusive = gts[0].op === "gte" && lts[0].op === "lte"
    if (bothInclusive ? lo > hi : lo >= hi) {
      return t("The amount range is empty: the lower bound must not be above the upper bound.")
    }
  }

  // Match fields (category, card network, issuer, processor): a value can't be both
  // required and excluded, and two different "is" values can never both be true.
  const matchFields = [...new Set(list.filter((c) => c.field !== "amount").map((c) => c.field))]
  for (const f of matchFields) {
    const rows = list.filter((c) => c.field === f && hasVal(c))
    const isVals = rows.filter((c) => c.op === "is").map((c) => c.value)
    const notVals = rows.filter((c) => c.op === "is_not").map((c) => c.value)
    if (isVals.some((v) => notVals.includes(v))) {
      return t("A rule can't both require and exclude the same value.")
    }
    if (new Set(isVals).size > 1) {
      return t("Two different 'is' conditions on the same field can never both match.")
    }
  }

  return ""
}

// ── Cross-rule checks ───────────────────────────────────────────────────────
// conditionsConflict above validates ONE rule in isolation. These check a candidate
// rule against the ones already saved, so the list can't collect entries that silently
// do nothing (a duplicate, or a rule whose disputes an existing rule already catches).

const LOWER_OPS = ["gte", "gt"]
const UPPER_OPS = ["lte", "lt"]

// Does condition `a` match everything condition `b` matches? Same field only.
// Amount: a wider bound implies a narrower one (">= 50" covers ">= 100").
// Match fields: only an identical field/op/value implies the other.
const conditionImplies = (a, b) => {
  if (a.field !== b.field) return false
  if (a.field === "amount") {
    const av = Number(a.value)
    const bv = Number(b.value)
    if (LOWER_OPS.includes(a.op) && LOWER_OPS.includes(b.op)) {
      // Equal values: an inclusive bound covers an exclusive one, never the reverse.
      return bv > av || (bv === av && (a.op === "gte" || b.op === "gt"))
    }
    if (UPPER_OPS.includes(a.op) && UPPER_OPS.includes(b.op)) {
      return bv < av || (bv === av && (a.op === "lte" || b.op === "lt"))
    }
    return false
  }
  return a.op === b.op && String(a.value) === String(b.value)
}

const usable = (conds) =>
  (conds || []).filter((c) => c && c.field && c.value !== "" && c.value != null)

// A rule matches a SUPERSET of another when every one of its conditions is implied by
// some condition of the other (fewer/looser conditions = catches more disputes).
const covers = (broadConds, narrowConds) => {
  const broad = usable(broadConds)
  const narrow = usable(narrowConds)
  if (!broad.length || !narrow.length) return false
  return broad.every((bc) => narrow.some((nc) => conditionImplies(bc, nc)))
}

const sameConditions = (a, b) => {
  // Saved rules come from Redux, so their conditions array is seamless-immutable and
  // .sort() would throw ("cannot be invoked on an Immutable data structure"). Copy into
  // a plain array first; the spread is what makes the sort legal.
  const key = (conds) =>
    [...usable(conds).map((c) => `${c.field}|${c.op}|${c.value}`)].sort().join("&&")
  return key(a) === key(b) && key(a) !== ""
}

// Find the first saved rule that makes a candidate rule pointless. Shared by both rule
// types; each caller words the message for its own domain.
//   'duplicate' - identical conditions
//   'covered'   - the existing rule already catches everything the candidate would
//   'covers'    - the candidate would swallow the existing (narrower) rule
export const findRuleOverlap = (conditions, otherRules) => {
  const candidate = usable(conditions)
  if (!candidate.length) return null
  for (const other of otherRules || []) {
    const otherConds = other?.conditions
    if (!usable(otherConds).length) continue
    if (sameConditions(candidate, otherConds)) return { kind: "duplicate", rule: other }
    if (covers(otherConds, candidate)) return { kind: "covered", rule: other }
    if (covers(candidate, otherConds)) return { kind: "covers", rule: other }
  }
  return null
}

// Auto-submission exceptions: every overlap case leaves a rule that can never change an
// outcome, which is exactly the contradictory settings state this is meant to prevent.
export const crossRuleConflict = (conditions, otherRules, t) => {
  const hit = findRuleOverlap(conditions, otherRules)
  if (!hit) return ""
  const summary = ruleConditionsSummary(hit.rule.conditions, t)
  if (hit.kind === "duplicate") return t("You already have an identical exception.")
  if (hit.kind === "covered") {
    return t("An existing exception already covers these disputes: _summary_.", { summary })
  }
  return t(
    "This would also cover an existing, narrower exception (_summary_), making it redundant. Remove that one first, or narrow this one.",
    { summary }
  )
}

// Assignment rules: overlap matters even more here, because two rules that match the
// same dispute but name different people have no defined winner. `assigneeName` resolves
// a member id so the message can say who the clashing rule routes to.
export const assignmentRuleConflict = (conditions, otherRules, assigneeName, t) => {
  const hit = findRuleOverlap(conditions, otherRules)
  if (!hit) return ""
  const summary = ruleConditionsSummary(hit.rule.conditions, t)
  const who = assigneeName(hit.rule.assignee)
  if (hit.kind === "duplicate") {
    return t(
      "Another rule already uses these exact conditions and assigns them to _who_. Two rules with the same conditions have no defined winner.",
      { who }
    )
  }
  if (hit.kind === "covered") {
    return t(
      "These disputes are already caught by a broader rule (_summary_) that assigns them to _who_, so this rule would never apply.",
      { summary, who }
    )
  }
  return t(
    "This rule would also catch a narrower rule (_summary_) that assigns to _who_, leaving that rule unreachable. Remove it first, or narrow this one.",
    { summary, who }
  )
}
